import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box } from '@mui/material';
import { IEquipmentServiceTobeScheduled  } from '../../../../store/reducers/scheduledServices';
import CalendarEvent from './CalendarEvent';
import { i18n } from '../../../../translate/i18n';
import { IUser } from '../../../../models/iuser';
import * as actions from '../../../../store/actions';
import { RootState } from '../../../../store/types';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';

type Props = {
  date: { month: number, year: number, day: number },
  user: IUser,
  completedServicesCount: number,
  notCompletedServicesCount: number,
}

const DropContainer: React.FC<Props> = ({ date, user, completedServicesCount, notCompletedServicesCount }) => {
  const dispatch = useDispatch();
  const [completedServices, setCompletedServices] = useState<number>(completedServicesCount);
  const [notCompletedServices, setNotCompletedServices] = useState<number>(notCompletedServicesCount);
  const [border, setBorder] = useState<string>('1px solid lightGray');
  const [eventsDragged, setEventsDragged] = useState<number>(0);
  const [background, setBackground] = useState<string>('white');

  const selectedSiteId = useSelector((state:RootState) => state.scheduledServices.selectedSiteId);
  const servicesForSchedulingFilters = useSelector((state: RootState) => state.scheduledServices.filtersServicesForScheduling);
  const selectedServicesDrag = useSelector((state: RootState) => state.scheduledServices.selectedServicesToBeScheduled);

  const { areas, companies, equipmentGroups, equipmentModels, frequencies, places, serviceTypes, startDate, endDate, equipment, serviceStatus } = servicesForSchedulingFilters;

  useEffect(() => {
    setCompletedServices(completedServicesCount);
    setNotCompletedServices(notCompletedServicesCount);
  }, [completedServicesCount, notCompletedServicesCount]);

  const onDropHandle = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const dropData: IEquipmentServiceTobeScheduled[] = JSON.parse(event.dataTransfer.getData('cardInfo'));
    setNotCompletedServices(notCompleteServices => notCompleteServices + dropData.length);

    const body = {
      services: dropData.map((data) => ({
        accountables: [user._id],
        equipment: data._id,
        equipmentModelName: data.equipmentModel.name,
        equipmentModelId: data.equipmentModel._id,
        equipmentServiceId: data.scheduledServicesReplicated.equipmentServiceId._id,
        expirationDate: data.scheduledServicesReplicated.expirationDate,
        frequency: {
          _id: data.scheduledServicesReplicated.equipmentServiceId.frequency._id,
          name: data.scheduledServicesReplicated.equipmentServiceId.frequency.name,
        },
        serviceType: {
          _id: data.scheduledServicesReplicated.equipmentServiceId.serviceType._id,
          name: data.scheduledServicesReplicated.equipmentServiceId.serviceType.name,
        },
        scheduledDate: moment().set({year: date.year, month: date.month, date: date.day, hour: 12, minute: 0, second: 0}).toDate(),
        site: data.site,
        place: data.place._id,
      }))
    }

    dispatch(actions.postScheduledService(body, 1, 10, '', selectedSiteId, places, equipmentModels, equipmentGroups, areas, companies, serviceTypes, frequencies, startDate, endDate, equipment, serviceStatus));
    dispatch(actions.scheduledServicesClearSelectedServicesTobeScheduled());
    setBorder('1px solid lightGray');
    setEventsDragged(0);
    setBackground('white');
  }

  return (
    <Box
      onDrop={onDropHandle}
      sx={{...styles.dropContainer, border, background }}
      onDragOver={
        (event) => {
          event.preventDefault();
          setBorder('2px solid #66BB6A');
          setEventsDragged(selectedServicesDrag.length);
          setBackground('rgba(76, 175, 80, 0.2)')
        }
      }
      onDragLeave={
        (event) => {
          event.preventDefault();
          setBorder('1px solid lightGray');
          setEventsDragged(0);
          setBackground('white')
        }
      }
    >
      { notCompletedServices > 0 && <CalendarEvent
          event={{quantity: notCompletedServices, title: notCompletedServices === 1 ? i18n.t('dropContainer.scheduledServiceTitle') : i18n.t('dropContainer.scheduledServicesTitle')}}
          userId={user._id}
          date={new Date(date.year, date.month, date.day).toISOString()}
          completed={false}
        />
      }
      { completedServices > 0 && <CalendarEvent
          event={{quantity: completedServices, title: completedServices === 1 ? i18n.t('dropContainer.scheduledServiceDoneTitle') : i18n.t('dropContainer.scheduledServicesDoneTitle')}}
          userId={user._id}
          date={new Date(date.year, date.month, date.day).toISOString()}
          completed={true}
        />
      }
      { eventsDragged > 0 &&
        <Box sx={{ marginTop: 'auto', alignSelf: 'flex-end', display: 'flex'}}>
          <LibraryAddIcon color='primary' fontSize='medium' />
          <span style={{ color: '#4CAF50'}}>{`+${eventsDragged}`}</span>
        </Box>
      }
    </Box>
  )
};

export default DropContainer;

const styles = {
  dropContainer: {
    width: '200px',
    height: '7rem',
    background: 'white',
    boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.20)',
    border: '1px solid lightGray',
    display: 'flex',
    padding: '0.4rem',
    flexDirection: 'column',
  },
  eventContainer: {
    border: '1px solid black',
    padding: '3px',
  },
}
